import { useQuery } from 'react-query';
import { Member, OrganizationMember } from './index.types';
import { client } from 'src/utils/api-client';

export interface PaginatedMembersResponse {
  users: Member[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  has_previous: boolean;
}

export const useOrganizationMembersPaginated = (
  page: number = 1,
  pageSize: number = 100,
  isRootMember: boolean | undefined = undefined
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  
  if (isRootMember !== undefined) {
    params.append('is_root_member', isRootMember.toString());
  }

  const query = useQuery<PaginatedMembersResponse>(
    ['organization-members-paginated', page, pageSize, isRootMember],
    () =>
      client
        .get(`web/settings_v2/organization-members?${params}`)
        .then((res) => res.data)
  );

  const userOrganizationMap: {
    [key: string]: { [key: string]: OrganizationMember };
  } = {};
  
  if (query.data?.users) {
    query.data.users.forEach((user) => {
      userOrganizationMap[user.id] = Object.fromEntries(
        user.organizations.map((org) => [org.organization_id, org])
      );
    });
  }

  return {
    ...query,
    organizationMembers: query.data?.users || [],
    userOrganizationMap,
    pagination: {
      total: query.data?.total || 0,
      page: query.data?.page || 1,
      pageSize: query.data?.page_size || pageSize,
      hasNext: query.data?.has_next || false,
      hasPrevious: query.data?.has_previous || false,
    },
  };
};

// Legacy hook for backward compatibility - loads all data at once
export const useOrganizationMembers = () => {
  const query = useQuery<Member[]>('organization-members-legacy', () =>
    client.get('web/settings_v2/organization-members?page_size=10000').then((res) => res.data?.users || res.data)
  );
  const data = query.data || [];
  const userOrganizationMap: {
    [key: string]: { [key: string]: OrganizationMember };
  } = {};
  data.forEach((user) => {
    userOrganizationMap[user.id] = Object.fromEntries(
      user.organizations.map((org) => [org.organization_id, org])
    );
  });

  return { ...query, organizationMembers: data, userOrganizationMap };
};
